import { useToast } from '@melio/penny';
import { Bill, FundingSource, useBill, useFundingSource, useVendor, Vendor } from '@melio/platform-api';
import { forwardRef } from '@melio/platform-utils';
import { useEffect } from 'react';

import { PaymentDraft } from '../../types';
import { ReviewAndConfirmScreen } from './screens';

export type ReviewAndConfirmActivityProps = {
  onBack?: VoidFunction;
  onError?: ErrorFunction;
  onClose?: VoidFunction;
  onDone: (value?: string) => void;
  billId?: string;
  paymentDraft: PaymentDraft;
  onEditFundingSource?: VoidFunction;
  onEditScheduleDate?: VoidFunction;
  isSaving?: boolean;
};

export const ReviewAndConfirmActivity = forwardRef<ReviewAndConfirmActivityProps>(
  (
    { onDone, onBack, onError: _onError, paymentDraft, onEditFundingSource, onEditScheduleDate, isSaving, ...props },
    ref
  ) => {
    const fundingSourceModel = useFundingSource({ id: paymentDraft.fundingSourceId });
    const billModel = useBill({ id: paymentDraft.billId });
    const vendorModel = useVendor({ id: billModel.data?.vendorId, initialData: billModel.data?.vendor });
    const models = [fundingSourceModel, billModel, vendorModel];

    const isReady = models.every((m) => !!m.data) && !!paymentDraft.scheduledDate;
    const isLoading = models.some((m) => !m.isFetched);

    const toast = useToast();

    const onError = (error: PlatformError) => {
      toast({ title: error.message, type: 'error' });
      _onError?.(error);
    };

    useEffect(() => {
      if (!isReady && !isLoading) {
        onBack?.();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, isReady]);

    useEffect(() => {
      models.forEach(({ error }) => error && onError(error));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [models.some(({ error }) => !!error?.message)]);

    return (
      <ReviewAndConfirmScreen
        isLoading={!isReady}
        onSubmit={onDone}
        vendor={vendorModel.data as Vendor}
        scheduleDate={paymentDraft.scheduledDate as Date}
        bill={billModel.data as Bill}
        fundingSource={fundingSourceModel.data as FundingSource}
        onEditFundingSource={onEditFundingSource}
        onEditScheduleDate={onEditScheduleDate}
        isSaving={isSaving}
        {...props}
        ref={ref}
      />
    );
  }
);
ReviewAndConfirmActivity.displayName = 'ReviewAndConfirmActivity';
